import classNames from 'classnames'
import { Link } from 'gatsby-link'
import React, { FC, useState } from 'react'
import time from '../utilities/time'
import { LoadMoreButton } from './LoadMoreButton'

export interface LatestContentItem {
  date: string
  title: string
  url: string
}

export interface LatestContentProps {
  allContentButtonText?: string
  allContentUrl?: string
  headerTitleClass?: string
  items: LatestContentItem[]
  loadMoreButtonText?: string
  numItemsPerPage: number
  title: string
  titleAs?: keyof JSX.IntrinsicElements
}

interface ItemProps extends LatestContentItem {
  highlighted?: boolean
}

const Item: FC<ItemProps> = ({ url, date, title, highlighted }) => {
  const linkClass = `
    block pt-8 pb-20 transition-hover hover:opacity-hover
    sm:py-15 md:pt-15
    md:pb-30
    lg:flex
    xl:pt-30 xl:pb-45
  `

  const dateClass = classNames(
    'font-t text-t-100 text-primary text-right mr-30',
    'sm:text-t-300',
    'lg:w-120 lg:flex-none lg:mt-7',
    'xl:w-150',
    highlighted ? 'xl:mt-15' : 'xl:mt-8',
    'xxl:text-t-400',
    highlighted ? 'xxl:mt-7' : 'xxl:mt-3'
  )

  const titleClass = highlighted
    ? classNames(
        'font-t text-t-500 text-secondary-darker',
        'sm:text-t-600',
        'lg:text-t-700',
        'xl:text-t-800',
        'xxl:text-t-900'
      )
    : classNames(
        'font-t text-t-300 text-secondary-darker',
        'sm:text-t-400',
        'sm:pr-120',
        'md:max-w-none md:pr-0',
        'lg:text-t-500',
        'xl:text-t-600',
        'xxl:text-t-700'
      )

  return (
    <li className="xl:ml-0 xxl:pr-150">
      <Link to={url} className={linkClass}>
        <span className={dateClass}>
          {date && <time dateTime={date}>{time.toLongDate(date)}</time>}
        </span>
        <h3 className={titleClass}>{title}</h3>
      </Link>
    </li>
  )
}

export const LatestContent: FC<LatestContentProps> = ({
  items,
  title,
  titleAs: Title = 'h2',
  ...loadMoreProps
}) => {
  const [numItemsToDisplay, setNumItemsToDisplay] = useState<number>(
    loadMoreProps.numItemsPerPage
  )

  return (
    <div
      className={classNames(
        'col-span-full',
        'tb:px-30',
        'sm:col-start-2 sm:col-span-4 sm:px-0',
        'md:col-span-full md:col-start-1',
        'lg:col-start-1',
        'xxl:col-start-2 xxl:col-span-12'
      )}
    >
      <header className="text-center mb-30">
        <Title className="text-primary font-h2 text-h2">{title}</Title>
      </header>

      <ul
        className={classNames(
          'divide-y divide-neutral-lighter border-t border-b border-neutral-light',
          'sm:divide-y-2 sm:border-t-2',
          'lg:mx-30',
          'xl:mx-0'
        )}
      >
        {items.slice(0, numItemsToDisplay).map((item, i) => (
          <Item key={i} {...item} highlighted={i === 0} />
        ))}
      </ul>

      <LoadMoreButton
        {...loadMoreProps}
        additionalClassName="mt-30 lg:mt-60"
        currentNumItems={numItemsToDisplay}
        maxItems={items.length}
        onLoadMore={setNumItemsToDisplay}
      />
    </div>
  )
}
